import { ReactElement, useMemo } from 'react';
import { Card, Col, Descriptions, Flex, Row, Space, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconBuildingBank, IconSettings, IconUser } from '@tabler/icons-react';

import { CardTitleWithIcon } from '@ohpen/enso-ui';

import { LenderDetailsProps } from '../interfaces';
import { getComplianceItems, getComplianceSettingsItems, renderComplianceEditAction } from '../utils';
import { DescriptionsItemType } from 'antd/es/descriptions';
import styles from '../styles.module.css';

export const LenderDetails = ({ details, isFetching, onEditCompliance }: LenderDetailsProps): ReactElement => {
  const { t } = useTranslation();

  const firmComplianceItems: DescriptionsItemType[] = useMemo(() => {
    return getComplianceItems(
      t,
      details?.complianceCheckConfiguration?.firmPermissionLevelPerStatus,
      details?.complianceCheckConfiguration?.firmToReviewPermissionLevel,
    );
  }, [details, t]);

  const intermediaryComplianceItems: DescriptionsItemType[] = useMemo(() => {
    return getComplianceItems(
      t,
      details?.complianceCheckConfiguration?.intermediaryPermissionLevelPerStatus,
      details?.complianceCheckConfiguration?.intermediaryToReviewPermissionLevel,
    );
  }, [details, t]);

  const settingsItems: DescriptionsItemType[] = useMemo(
    () => getComplianceSettingsItems(t, details?.complianceCheckConfiguration),
    [details, t],
  );

  if (isFetching || !details) {
    return (
      <Flex justify="center" align="center" className={styles.loadingContainer}>
        <Spin />
      </Flex>
    );
  }

  return (
    <Flex vertical className={styles.scrollableContent}>
      <Flex vertical gap={16}>
        <Flex justify="flex-end">{renderComplianceEditAction(t, onEditCompliance, details?.lockedByActionId)}</Flex>
        <Row>
          <Col span={12} className={styles.col}>
            <Card title={<CardTitleWithIcon icon={<IconBuildingBank />} title={t('lender.lender.labels.firmComplianceConfiguration')} />}>
              <Space direction="vertical" size="large">
                <Descriptions column={1} size="small" layout="vertical">
                  <Descriptions.Item label={t('lender.lender.labels.initialComplianceStatus')}>
                    {details?.complianceCheckConfiguration.defaultFirmStatus
                      ? t(`lender.lender.labels.${details.complianceCheckConfiguration.defaultFirmStatus}`, {
                          defaultValue: details.complianceCheckConfiguration.defaultFirmStatus,
                        })
                      : ''}
                  </Descriptions.Item>
                </Descriptions>
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary" strong>
                    {t('lender.lender.labels.complianceStatusPermissions')}:
                  </Typography.Text>
                  <Descriptions column={5} size="small" items={firmComplianceItems} layout="vertical" />
                </Space>
              </Space>
            </Card>
          </Col>
          <Col span={12} className={styles.col}>
            <Card title={<CardTitleWithIcon icon={<IconUser />} title={t('lender.lender.labels.intermediaryComplianceConfiguration')} />}>
              <Space direction="vertical" size="large">
                <Descriptions column={1} size="small" layout="vertical">
                  <Descriptions.Item label={t('lender.lender.labels.initialComplianceStatus')}>
                    {details?.complianceCheckConfiguration.defaultIntermediaryStatus
                      ? t(`lender.lender.labels.${details.complianceCheckConfiguration.defaultIntermediaryStatus}`, {
                          defaultValue: details.complianceCheckConfiguration.defaultIntermediaryStatus,
                        })
                      : ''}
                  </Descriptions.Item>
                </Descriptions>
                <Space direction="vertical" size="small">
                  <Typography.Text type="secondary" strong>
                    {t('lender.lender.labels.complianceStatusPermissions')}:
                  </Typography.Text>
                  <Descriptions column={5} size="small" items={intermediaryComplianceItems} layout="vertical" />
                </Space>
              </Space>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col span={12} className={styles.col}>
            <Card title={<CardTitleWithIcon icon={<IconSettings />} title={t('lender.lender.labels.complianceSettings')} />}>
              <Descriptions column={2} size="small" items={settingsItems} layout="vertical" />
            </Card>
          </Col>
        </Row>
      </Flex>
    </Flex>
  );
};
